import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  RecorderEngine,
  type RecorderSnapshot,
} from "./lib/recorder-engine";
import { BrowserQueueStore } from "./lib/queue-store";
import { UploadQueue } from "./lib/upload-queue";
import { readAudioStreamingConfig } from "./audio/audio-streaming-session";
import { ThemeToggle } from "./theme";
import type { QueueSnapshot } from "./types";
import "./styles.css";

const TOKEN_STORAGE_KEY = "long-recorder-token";
const isWebSocketPilotBuild =
  readAudioStreamingConfig().mode === "websocket";

const initialRecorder: RecorderSnapshot = {
  state: "idle",
  microphoneState: "not_requested",
  persistedChunkCount: 0,
  likelySilentCount: 0,
  selectedMimeType: "Browser default",
  streaming: {
    state: "idle",
    paused: false,
    queuedChunks: 0,
    queuedDurationMs: 0,
    nextSequenceNumber: 0,
    reconnectAttempt: 0,
    bufferedAmount: 0,
  },
};

const initialQueue: QueueSnapshot = {
  totalOperations: 0,
  queuedChunks: 0,
  blockedOperations: 0,
  waitingForToken: false,
  lastStatus: "Queue ready",
  lastUploadStatus: "No upload attempted",
};

function formatElapsed(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1_000));
  const hours = Math.floor(totalSeconds / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds]
    .map((value) => String(value).padStart(2, "0"))
    .join(":");
}

function recordingLabel(state: RecorderSnapshot["state"]): string {
  switch (state) {
    case "calibrating":
      return "Getting ready…";
    case "recording":
      return "Recording";
    case "stopping":
      return "Finishing up…";
    case "completed":
      return "Ready for another recording";
    case "microphone_disconnected":
      return "Microphone disconnected";
    case "error":
      return "Recording needs attention";
    default:
      return "Ready to record";
  }
}

interface Notice {
  title: string;
  description: string;
  tone?: "warning" | "error";
}

export default function App() {
  const tokenRef = useRef(sessionStorage.getItem(TOKEN_STORAGE_KEY) ?? "");
  const [tokenInput, setTokenInput] = useState(tokenRef.current);
  const [tokenSaved, setTokenSaved] = useState(Boolean(tokenRef.current));
  // Intentionally state-only: this credential is passed to one live streaming
  // session and is never copied to browser storage or the durable upload queue.
  const [proxySessionKey, setProxySessionKey] = useState("");
  const [recorder, setRecorder] =
    useState<RecorderSnapshot>(initialRecorder);
  const [queueSnapshot, setQueueSnapshot] =
    useState<QueueSnapshot>(initialQueue);
  const [now, setNow] = useState(Date.now());
  const [actionPending, setActionPending] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState("");
  const [microphoneListError, setMicrophoneListError] = useState<string>();
  const recordedDurationRef = useRef(0);
  const recordingStartedAtRef = useRef<number | undefined>(undefined);

  const store = useMemo(() => new BrowserQueueStore(), []);
  const queue = useMemo(
    () =>
      new UploadQueue({
        store,
        getToken: () => tokenRef.current,
      }),
    [store],
  );
  const engineRef = useRef<RecorderEngine | null>(null);

  if (!engineRef.current) {
    engineRef.current = new RecorderEngine(store, queue, setRecorder);
  }
  const engine = engineRef.current;

  const refreshMicrophones = async () => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      setMicrophoneListError("Microphone selection is not available here.");
      return;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(
        (device) => device.kind === "audioinput" && device.deviceId,
      );
      setMicrophones(audioInputs);
      setSelectedMicrophoneId((current) =>
        current &&
        !audioInputs.some((device) => device.deviceId === current)
          ? ""
          : current,
      );
      setMicrophoneListError(undefined);
    } catch {
      setMicrophoneListError(
        "We could not show the available microphones. You can still use your default microphone.",
      );
    }
  };

  useEffect(() => {
    const unsubscribe = queue.subscribe(setQueueSnapshot);
    void queue.refresh();
    void queue.retryPendingNow();
    void refreshMicrophones();

    const timer = setInterval(() => setNow(Date.now()), 250);
    const handleOnline = () => {
      setIsOffline(false);
      void queue.retryPendingNow();
    };
    const handleOffline = () => setIsOffline(true);
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const active = ["calibrating", "recording", "muted", "stopping"].includes(
        engine.getSnapshot().state,
      );
      if (active || queue.getSnapshot().totalOperations > 0) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("beforeunload", handleBeforeUnload);
    navigator.mediaDevices?.addEventListener(
      "devicechange",
      refreshMicrophones,
    );

    return () => {
      unsubscribe();
      clearInterval(timer);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      navigator.mediaDevices?.removeEventListener(
        "devicechange",
        refreshMicrophones,
      );
      engine.dispose();
    };
  }, [engine, queue]);

  useEffect(() => {
    const transitionAt = Date.now();

    if (
      recorder.state === "recording" &&
      recordingStartedAtRef.current === undefined
    ) {
      recordingStartedAtRef.current = transitionAt;
    } else if (
      recorder.state !== "recording" &&
      recordingStartedAtRef.current !== undefined
    ) {
      recordedDurationRef.current +=
        transitionAt - recordingStartedAtRef.current;
      recordingStartedAtRef.current = undefined;
    }

    if (recorder.state === "idle" || recorder.state === "completed") {
      recordedDurationRef.current = 0;
      recordingStartedAtRef.current = undefined;
    }

    setNow(transitionAt);
  }, [recorder.state]);

  const runAction = async (action: () => Promise<void>) => {
    setActionPending(true);
    try {
      await action();
    } catch {
      // RecorderEngine updates its snapshot when an action cannot complete.
    } finally {
      setActionPending(false);
    }
  };

  const saveToken = (event: FormEvent) => {
    event.preventDefault();
    const token = tokenInput.trim();
    if (!token) return;
    sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    tokenRef.current = token;
    setTokenSaved(true);
    void queue.notifyTokenChanged();
  };

  const recordingElapsed =
    recorder.state === "idle" || recorder.state === "completed"
      ? 0
      : recordedDurationRef.current +
        (recorder.state === "recording" &&
        recordingStartedAtRef.current !== undefined
          ? now - recordingStartedAtRef.current
          : 0);
  const canStart = ["idle", "completed", "error"].includes(recorder.state);
  const canStop = [
    "calibrating",
    "recording",
    "muted",
    "microphone_disconnected",
    "error",
  ].includes(recorder.state);
  const accessCodeNeedsAttention =
    !tokenSaved || queueSnapshot.waitingForToken;

  let notice: Notice | undefined;
  if (recorder.error) {
    notice = {
      title: "We could not use your microphone",
      description:
        "Check that your browser can use the microphone, then try starting again.",
      tone: "error",
    };
  } else if (recorder.state === "microphone_disconnected") {
    notice = {
      title: "Your microphone was disconnected",
      description:
        "Reconnect it, choose it again if needed, and start a new recording.",
      tone: "error",
    };
  } else if (queueSnapshot.waitingForToken) {
    notice = {
      title: "Your recording code needs updating",
      description:
        "Enter the current recording code below so saved recordings can finish uploading.",
      tone: "error",
    };
  } else if (queueSnapshot.blockedOperations > 0) {
    notice = {
      title: "A saved recording needs your attention",
      description:
        "Try again when your connection is available. Your recording remains safely on this device.",
      tone: "warning",
    };
  } else if (isOffline && queueSnapshot.totalOperations > 0) {
    notice = {
      title: "You are offline",
      description:
        "Your recording is safe on this device and will upload when you are back online.",
      tone: "warning",
    };
  } else if (microphoneListError) {
    notice = {
      title: "Microphone list unavailable",
      description: microphoneListError,
      tone: "warning",
    };
  } else if (recorder.state === "calibrating") {
    notice = {
      title: "Getting your microphone ready",
      description: "This only takes a few seconds.",
    };
  } else if (recorder.state === "stopping") {
    notice = {
      title: "Saving your recording",
      description: "Please keep this page open for a moment.",
    };
  } else if (recorder.state === "completed" && queueSnapshot.totalOperations > 0) {
    notice = {
      title: "Recording saved on this device",
      description: "It will continue uploading in the background.",
    };
  }

  return (
    <main className="shell recorder-shell">
      <header className="masthead simple-masthead">
        <a className="brand" href="/" aria-label="Recorder home">
          Voice Recorder
        </a>
        <div className="header-actions">
          <ThemeToggle />
          <a className="nav-link" href="/recordings">
            Archive
          </a>
        </div>
      </header>

      {accessCodeNeedsAttention && (
        <section className="access-gate" aria-labelledby="access-code-title">
          <div>
            <p className="eyebrow">Welcome</p>
            <h1 id="access-code-title">Enter your recording code</h1>
            <p className="intro">
              Your code is kept only in this browser tab.
            </p>
          </div>
          <form onSubmit={saveToken}>
            <label htmlFor="recorder-token">Recording code</label>
            <div className="token-row">
              <input
                id="recorder-token"
                type="password"
                value={tokenInput}
                onChange={(event) => setTokenInput(event.target.value)}
                autoComplete="off"
                placeholder="Enter your code"
              />
              <button type="submit" className="primary">
                Continue
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="recorder-card" aria-labelledby="recorder-title">
        <div className="recording-status">
          <span
            className={`recording-dot ${
              recorder.state === "recording" ? "is-recording" : ""
            }`}
            aria-hidden="true"
          />
          <span id="recorder-title">{recordingLabel(recorder.state)}</span>
        </div>

        <div className="clock" aria-label="Recording timer">
          <strong>{formatElapsed(recordingElapsed)}</strong>
        </div>

        <div className="device-picker">
          <label htmlFor="microphone-select">Microphone</label>
          <select
            id="microphone-select"
            value={selectedMicrophoneId}
            disabled={!canStart || actionPending}
            onChange={(event) => setSelectedMicrophoneId(event.target.value)}
          >
            <option value="">System default</option>
            {microphones.map((microphone, index) => (
              <option
                key={microphone.deviceId || `microphone-${index}`}
                value={microphone.deviceId}
              >
                {microphone.label || `Microphone ${index + 1}`}
              </option>
            ))}
          </select>
        </div>

        {isWebSocketPilotBuild && (
          <div className="proxy-key-picker">
            <label htmlFor="proxy-session-key">Live transcription key</label>
            <input
              id="proxy-session-key"
              type="password"
              value={proxySessionKey}
              onChange={(event) => setProxySessionKey(event.target.value)}
              disabled={!canStart || actionPending}
              autoComplete="off"
              spellCheck={false}
              placeholder="Enter your temporary key"
            />
            <p>Used only while this recording is active. It is not saved.</p>
          </div>
        )}

        <div className="controls">
          <button
            className="primary"
            disabled={!tokenSaved || !canStart || actionPending}
            onClick={() =>
              void runAction(async () => {
                const sessionKey = proxySessionKey.trim() || undefined;
                // Clear the form immediately after handing the runtime-only
                // value to the engine. The live sidecar owns it in memory
                // until it stops, then clears it as part of disposal.
                setProxySessionKey("");
                await engine.start(
                  selectedMicrophoneId || undefined,
                  isWebSocketPilotBuild ? sessionKey : undefined,
                );
                await refreshMicrophones();
              })
            }
          >
            {recorder.state === "calibrating" ? "Starting…" : "Start recording"}
          </button>
          <button
            className="danger"
            disabled={!canStop || actionPending}
            onClick={() => void runAction(() => engine.stop())}
          >
            Stop recording
          </button>
        </div>

        {notice && (
          <section
            className={`friendly-notice ${notice.tone ?? ""}`}
            aria-live="polite"
            role={notice.tone === "error" ? "alert" : undefined}
          >
            <div>
              <strong>{notice.title}</strong>
              <p>{notice.description}</p>
            </div>
            {queueSnapshot.blockedOperations > 0 && (
              <button
                className="secondary"
                type="button"
                onClick={() => void queue.retryBlocked()}
              >
                Try again
              </button>
            )}
          </section>
        )}
      </section>
    </main>
  );
}
